import axios from "axios";
import { GCP_SIGNED_URLS_ENDPOINT } from "gcp-config";

export interface GenerateSignedUrlRequest {
  feature: string;
  file_name: string;
  file_mime_type: string;
  file_size: string;
}

export interface SignedUrlResponse {
  feature: string;
  file_name: string;
  file_mime_type: string;
  file_size: string;
  signed_url: string;
  file_url: string | null;
  object_location: string;
  status: "success" | "error";
}

export interface GenerateSignedUrlsApiResponse {
  status: "success" | "error";
  message: string;
  data: SignedUrlResponse[];
  errorCode: string | null;
}

class GCPSignedUrlService {
  /**
   * Generate signed URLs for multiple files
   * @param requests - Array of file requests
   * @returns Promise with signed URLs and metadata
   */
  static async generateSignedUrls(
    requests: GenerateSignedUrlRequest[]
  ): Promise<GenerateSignedUrlsApiResponse> {
    try {
      const headersToSend: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")} `,
        platform: "web",
        platformv2: "web",
        project:"ncms",     
        source:"ncms"
      };

      const response = await axios.post<GenerateSignedUrlsApiResponse>(
        GCP_SIGNED_URLS_ENDPOINT,
        {
          files_list: requests,
        },
        {
          headers: headersToSend,
        }
      );
      console.log(response.data)
      if (response.data.status === "success") {
        return response.data;
      } else {
        throw new Error(
          response.data.message || "Failed to generate signed URLs"
        );
      }
    } catch (error: any) {
      console.error("Error generating signed URLs:", error);
      console.error("Axios error response:", error?.response);

      const backendMessage =
        error?.response?.data?.message || error?.response?.statusText;
      const backendData = error?.response?.data;

      const message =
        backendMessage || error?.message || "Failed to generate signed URLs";

      const err = new Error(message);
      (err as any).status = error?.response?.status;
      (err as any).backendData = backendData;
      throw err;
    }
  }

  /**
   * Generate a single signed URL
   * @param feature - Feature name
   * @param fileName - File name with path
   * @param fileMimeType - MIME type of the file
   * @param fileSize - File size in bytes
   * @returns Promise with signed URL response
   */
  static async generateSingleSignedUrl(
    feature: string,
    fileName: string,
    fileMimeType: string,
    fileSize: string
  ): Promise<SignedUrlResponse> {
    try {
      const response = await this.generateSignedUrls([
        {
          feature,
          file_name: fileName,
          file_mime_type: fileMimeType,
          file_size: fileSize,
        },
      ]);

      if (response && response.data && response.data.length > 0) {
        return response.data[0];
      }

      const err = new Error("No signed URL returned from API");
      (err as any).backendData = response;
      throw err;
    } catch (err: any) {
      console.error("generateSingleSignedUrl error:", err);
      throw err;
    }
  }
}

export default GCPSignedUrlService;
